import React, { useState, useEffect, useMemo } from "react";
import { useUser } from "util/context/user_context";
import RequestDialog from "components/repair/request_dialog";
import { getMatchingList } from "firebase_/firebase_function/matching";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import NewCommunityStory from "components/main/new_community_story";
import AdBannerSlot from "components/main/ad_banner_slot";
import Chatbot from "components/chatbot";

// ==== 디자인 토큰 ====
const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  amber50: "#FFFBEB",
  amber100: "#FEF3C7",
  amber800: "#92400E",
};

function StatusChip({ status }) {
  const map = {
    0: { label: "매칭중", bg: C.amber100, fg: C.amber800, dot: "#F59E0B" },
    1: { label: "매칭완료", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
    2: { label: "완료", bg: C.greenBg, fg: C.greenDark, dot: C.green },
  };
  const s = map[status] ?? map[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 9999,
        background: s.bg,
        color: s.fg,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 9999,
          background: s.dot,
          flexShrink: 0,
        }}
      />
      {s.label}
    </span>
  );
}

// 카테고리별 대표 이모지
const categoryEmoji = (name = "") => {
  const s = String(name);
  if (s.includes("트랙터")) return "🚜";
  if (s.includes("콤바인")) return "🌾";
  if (s.includes("제어기") || s.includes("전자") || s.includes("전기")) return "⚡";
  if (s.includes("엔진") || s.includes("모터")) return "⚙️";
  if (s.includes("배터리")) return "🔋";
  if (s.includes("오일") || s.includes("연료")) return "🛢️";
  if (s.includes("이앙기") || s.includes("파종")) return "🌱";
  if (s.includes("바퀴") || s.includes("타이어")) return "⚫";
  return "🔧";
};

// 상대 시간 표기 (예: 방금 전, 3시간 전, 2일 전)
const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return "";
  const diffMin = moment().diff(m, "minutes");
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = moment().diff(m, "hours");
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = moment().diff(m, "days");
  if (diffDay < 7) return `${diffDay}일 전`;
  return m.format("YYYY.MM.DD");
};

function MatchingCard({ item, onClick }) {
  const names = item?.categoryListName ?? [];
  const primary = names[0] || "기타";
  const secondary = names.slice(1).filter((n) => n && n !== "기타").join(" · ");
  const emoji = categoryEmoji(primary);
  const imageCount = Array.isArray(item.imageUrls) ? item.imageUrls.length : 0;
  const firstImage = imageCount > 0 ? item.imageUrls[0] : null;
  const expertName = item.expertName?.trim();
  const statusStr = item.status;

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        gap: 16,
        padding: 16,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.08)";
        e.currentTarget.style.borderColor = C.greenBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = C.line;
      }}
    >
      {/* Thumbnail / Emoji */}
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 12,
          background: firstImage ? "#F3F4F6" : C.greenBg,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {firstImage ? (
          <img
            src={firstImage}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span>{emoji}</span>
        )}
        {imageCount > 1 && (
          <span
            style={{
              position: "absolute",
              bottom: 3,
              right: 3,
              background: "rgba(17,24,39,0.75)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 6px",
              borderRadius: 6,
            }}
          >
            +{imageCount - 1}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {primary}
            {secondary && (
              <span style={{ color: C.sub, fontWeight: 500 }}> · {secondary}</span>
            )}
          </div>
          <StatusChip status={statusStr} />
        </div>

        {item.etc && (
          <div
            style={{
              fontSize: 13,
              color: C.body,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.etc}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 12,
            color: C.sub,
            marginTop: 2,
          }}
        >
          <span>{relativeTime(item.createDate)}</span>
          <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
          <span>
            {statusStr === "1" && expertName
              ? `${expertName} 기사 배정`
              : statusStr === "0"
              ? "기사 배정 대기 중"
              : statusStr === "2"
              ? "수리 완료"
              : ""}
          </span>
          {imageCount > 0 && (
            <>
              <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
              <span>사진 {imageCount}장</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = C.green }) {
  return (
    <div
      style={{
        flex: 1,
        padding: "20px 24px",
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${C.line}`,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ fontSize: 14, color: C.sub, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: accent, letterSpacing: "-0.02em" }}>
        {value}
      </div>
    </div>
  );
}

function NormalMainScreen() {
  const { userData } = useUser();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [myMatchingList, setMyMatchingList] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMatchLatest();
  }, []);

  const fetchMatchLatest = async () => {
    try {
      const response = await getMatchingList(userData.id, 10);
      if (response) setMyMatchingList(response[1] ?? []);
    } catch (e) {
      // 목업/빈 데이터 상황 — 무시
    }
  };

  const counts = useMemo(() => {
    const inProgress = myMatchingList.filter((m) => m.status === "0").length;
    const matched = myMatchingList.filter((m) => m.status === "1").length;
    const done = myMatchingList.filter((m) => m.status === "2").length;
    return { total: myMatchingList.length, inProgress, matched, done };
  }, [myMatchingList]);

  const formatCategory = (item) => {
    const names = item?.categoryListName ?? [];
    return names.filter((n) => n && n !== "기타").join(" · ") || "기타";
  };

  return (
    <div style={{ background: C.bg, minHeight: "calc(100vh - 70px)" }}>
      {/* ====== Ad Banner Slot (위) ====== */}
      <div style={{ paddingTop: 24 }}>
        <AdBannerSlot />
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "8px 24px 80px" }}>
        {/* ====== Welcome Hero ====== */}
        <section
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "32px 40px",
            background: `linear-gradient(135deg, ${C.greenBg} 0%, #FFFFFF 100%)`,
            borderRadius: 20,
            border: `1px solid ${C.greenBorder}`,
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 280 }}>
            <span
              style={{
                alignSelf: "flex-start",
                padding: "4px 12px",
                borderRadius: 9999,
                background: "#fff",
                border: `1px solid ${C.greenBorder}`,
                color: C.greenDark,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              농가 대시보드
            </span>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
              {userData?.name ? `${userData.name}님, 안녕하세요` : "안녕하세요"} 👋
            </h1>
            <p style={{ margin: 0, fontSize: 16, color: C.body, lineHeight: 1.6 }}>
              농기계 고장이 생겼나요? 버튼 한 번이면 가까운 수리기사와 바로 연결됩니다.
            </p>
          </div>

          <button
            onClick={() => setIsDialogOpen(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "18px 28px",
              background: C.greenDark,
              color: "#fff",
              border: "none",
              borderRadius: 14,
              fontSize: 17,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: `0 8px 20px ${C.green}33`,
              letterSpacing: "-0.01em",
            }}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
            새 수리요청 등록
          </button>
        </section>

        {/* ====== Quick Stats ====== */}
        <section style={{ display: "flex", gap: 16, marginTop: 24, flexWrap: "wrap" }}>
          <StatCard label="전체 요청" value={counts.total} accent={C.ink} />
          <StatCard label="매칭중" value={counts.inProgress} accent={C.amber800} />
          <StatCard label="매칭완료" value={counts.matched} accent="#1E40AF" />
          <StatCard label="완료" value={counts.done} accent={C.greenDark} />
        </section>

        {/* ====== Main Grid ====== */}
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
            gap: 24,
            marginTop: 24,
          }}
        >
          {/* Left: 내 견적 요청 목록 */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: `1px solid ${C.line}`,
              padding: 24,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
                내 견적 요청 목록
              </h2>
              <button
                onClick={() => navigate("/matching")}
                style={{
                  background: "none",
                  border: "none",
                  color: C.greenDark,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "6px 10px",
                  borderRadius: 8,
                }}
              >
                전체보기 →
              </button>
            </div>

            {myMatchingList.length === 0 ? (
              <div
                style={{
                  padding: "48px 0",
                  textAlign: "center",
                  color: C.sub,
                  fontSize: 14,
                }}
              >
                아직 요청하신 내역이 없어요.
                <br />
                <button
                  onClick={() => setIsDialogOpen(true)}
                  style={{
                    marginTop: 16,
                    padding: "10px 18px",
                    background: C.greenBg,
                    color: C.greenDark,
                    border: `1px solid ${C.greenBorder}`,
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  첫 수리요청 등록하기
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {myMatchingList.slice(0, 5).map((item, index) => (
                  <MatchingCard
                    key={item.docId ?? index}
                    item={item}
                    onClick={() => navigate(`/matching/${item.docId}`)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: 커뮤니티 최신 글 */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: `1px solid ${C.line}`,
              padding: 24,
              height: "fit-content",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
                커뮤니티 최신 글
              </h2>
              <button
                onClick={() => navigate("/community")}
                style={{
                  background: "none",
                  border: "none",
                  color: C.greenDark,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "6px 10px",
                  borderRadius: 8,
                }}
              >
                더보기 →
              </button>
            </div>
            <NewCommunityStory />
          </div>
        </section>

        <RequestDialog open={isDialogOpen} setIsDialogOpen={setIsDialogOpen} />
      </div>
      <Chatbot />
    </div>
  );
}

export default NormalMainScreen;
